import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, createRole } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { UserRole } from '../../../../types';
import SharedDialog from '../../../shared/shared-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { Messages } from '../../../utils/message-constants';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
interface CreateRoleDialogProps {
  open: boolean;
  handleClose: () => void;
  changeLogRoles: string[];
  roleNames: string[];
}

export default function CreateRoleDialog({
  open,
  handleClose,
  changeLogRoles,
  roleNames,
}: CreateRoleDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const navigate = useNavigate();

  const roleTypeOptions = [
    { displayValue: 'Admin', value: 'admin' },
    { displayValue: 'User', value: 'user' },
  ];

  const {
    handleSubmit,
    control,
    reset,
    getValues,
    formState: { errors, isDirty },
  } = useForm<Partial<UserRole>>({
    defaultValues: {
      roleName: '',
      default: false,
      desc: '',
      canSeePasswords: false,
      roleType: 'admin',
      createdDate: 0,
      lastUpdated: new Date().getTime(),
      changeLogLength: 75,
    },
  });

  const onSubmit = (data: Partial<UserRole>) => {
    if (
      isAbleToSeeUIElement(changeLogRoles, AdministrationRoles.CreateRole.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      const trimmedData = {
        ...data,
        roleName: data.roleName!.trim(),
      };
      mutate(trimmedData);
    }
  };

  const { mutate, isPending } = useMutation({
    mutationFn: (data: Partial<UserRole>) => createRole(data, changeLogMessage),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Created('Role'),
      });

      navigate(`./${encodeURIComponent(data.roleName)}`);
      queryClient.setQueryData([UserKeys.Roles], (oldData: UserRole[]) => {
        if (!oldData) return;
        return [...oldData, data];
      });
      queryClient.invalidateQueries({
        queryKey: [UserKeys.gPanelRoles],
      });
      setChangeLogMessage('');
      handleClose();
      reset();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Deleting('the role'),
      });
    },
    onSettled: (_returnData, _error, data) => {
      createEvent(analytics.administration.role_manager.create_role, {
        module: ModuleList.modules.administration.name,
        section: ModuleList.modules.administration.sections.roleManager,
        roleType: data.roleType,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const roleNamesLowerCase = roleNames.map((name) => name.toLowerCase());

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        reset();
        handleClose();
      }}
      title="Create Role"
      dialogContent={
        <Stack gap={1}>
          <Controller
            name={'roleName'}
            control={control}
            rules={{
              required: 'Role Name is required.',
              validate: (value) =>
                value && roleNamesLowerCase.includes(value.toLowerCase().trim())
                  ? 'Role name is currently in use. Please provide a different name.'
                  : value && value.trim().length === 0
                  ? 'Role Name is required.'
                  : true,
            }}
            render={({ field }) => (
              <TextField
                autoFocus
                inputProps={{
                  id: 'roleName',
                }}
                InputLabelProps={{
                  htmlFor: 'roleName',
                }}
                label="Role Name"
                fullWidth
                autoComplete="off"
                error={!!errors.roleName}
                helperText={errors?.roleName?.message || ' '}
                required
                {...field}
              />
            )}
          />
          <Controller
            name={'roleType'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                inputProps={{
                  id: 'roleType',
                }}
                InputLabelProps={{
                  htmlFor: 'roleType',
                }}
                select
                label="Role Type"
                sx={{ minWidth: 400 }}
              >
                {roleTypeOptions?.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.displayValue}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name={'desc'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                inputProps={{
                  id: 'desc',
                }}
                InputLabelProps={{
                  htmlFor: 'desc',
                }}
                label="Description"
                fullWidth
                autoComplete="off"
                sx={{ mt: 1 }}
              />
            )}
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              const trimmedData = {
                ...getValues(),
                roleName: getValues().roleName!.trim(),
              };
              mutate(trimmedData);
            }}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        loading: isPending,
        action: handleSubmit(onSubmit),
        disabled: isPending || !isDirty,
        title: 'Create Role',
      }}
    />
  );
}
